import { useMemo, useRef, useState } from "react";
import { Input, Modal } from "@iq/models";
import { importContacts, previewContactImport } from "../../services/contact_service";
import { LuImport } from "react-icons/lu";
import { RiFolderUploadLine } from "react-icons/ri";
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_EXTENSIONS = [".csv", ".xlsx"];

function ImportContacts({ onClose, onImported }) {
    const contactsFileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [mapping, setMapping] = useState({
        name: "",
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        account: "",
        job_title: "",
        website: "",
        industry: "",
        subindustry: "",
        lead_source: "",
        lead_tier: "",
        primary_problem: "",
        problem_evidence: "",
        recommended_offer: "",
        call_angle: "",
    });
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const actionButtons = useMemo(() => ([
        {
            key: "import-contacts",
            text: isSubmitting ? "Importing..." : "Import contacts",
            icon: <LuImport />,
            type: "button",
            onClick: handleUpload,
            disabled: !selectedFile || isSubmitting || !hasRequiredMapping(),
            color: "accent",
        },
    ]), [selectedFile, isSubmitting, mapping, handleUpload]);

    function validateFile(file) {
        if (!file) {
            return "Please select a file.";
        }

        const lowerName = file.name.toLowerCase();
        const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
        if (!hasValidExtension) {
            return "Unsupported file type. Use .csv or .xlsx.";
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
            return "File is too large. Maximum size is 5 MB.";
        }

        return "";
    }

    function applySuggestedMapping(suggested = {}) {
        setMapping({
            name: suggested.name || "",
            first_name: suggested.first_name || "",
            last_name: suggested.last_name || "",
            email: suggested.email || "",
            phone: suggested.phone || "",
            account: suggested.account || "",
            job_title: suggested.job_title || "",
            website: suggested.website || "",
            industry: suggested.industry || "",
            subindustry: suggested.subindustry || "",
            lead_source: suggested.lead_source || "",
            lead_tier: suggested.lead_tier || "",
            primary_problem: suggested.primary_problem || "",
            problem_evidence: suggested.problem_evidence || "",
            recommended_offer: suggested.recommended_offer || "",
            call_angle: suggested.call_angle || "",
        });
    }

    async function handleSelectFile(file) {
        const message = validateFile(file);
        if (message) {
            setSelectedFile(null);
            setPreview(null);
            setError(message);
            setResult(null);
            return;
        }

        setSelectedFile(file);
        setError("");
        setResult(null);

        setIsSubmitting(true);
        try {
            const payload = await previewContactImport(file);
            setPreview(payload);
            applySuggestedMapping(payload.suggestedMapping);
        } catch (previewError) {
            setError(previewError.message || "Failed to read file.");
            setPreview(null);
        } finally {
            setIsSubmitting(false);
        }
    }

    function handleFileInputChange(field, event) {
        const [file] = event.target.files || [];
        handleSelectFile(file);
    }

    function updateMapping(field, value) {
        setMapping((prev) => ({ ...prev, [field]: value }));
    }

    function hasRequiredMapping() {
        const hasName = Boolean(mapping.name || mapping.first_name || mapping.last_name);
        const hasIdentity = Boolean(mapping.email || mapping.phone);
        return hasName && hasIdentity && Boolean(mapping.account) && Boolean(mapping.primary_problem);
    }

    async function handleUpload() {
        if (!selectedFile || isSubmitting || !hasRequiredMapping()) {
            return;
        }

        setIsSubmitting(true);
        setError("");

        try {
            const payload = await importContacts({
                contactsFile: selectedFile,
                columnMapping: mapping,
            });
            setResult(payload);
            if (payload.imported > 0) {
                onImported?.();
            }
        } catch (uploadError) {
            setError(uploadError.message || "Import failed.");
            setResult(null);
        } finally {
            setIsSubmitting(false);
        }
    }

    function handleDownloadSample() {
        const csvContent = [
            "name,job title,account,email address,phone,website,industry,subindustry,lead source,lead tier,primary problem,problem evidence,recommended offer,call angle",
            "John Doe,CFO,Acme Inc,john.doe@example.com,+1-555-0101,https://acme.example,Manufacturing,Industrial,LeadForge,A,Manual quoting,Three-day turnaround,Workflow review,Reduce quote time",
            "Jane Doe,Operations Director,Acme Inc,jane.doe@example.com,+1-555-0102,https://acme.example,Manufacturing,Industrial,LeadForge,A,Manual quoting,Three-day turnaround,Workflow review,Reduce quote time",
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = "contacts_import_sample.csv";
        link.click();
        URL.revokeObjectURL(objectUrl);
    }

    function renderMappingField(field, label, keyPrefix, required = false) {
        return (
            <label className="import-mapping-field" key={field}>
                <span>
                    {label}
                    {required ? <strong className="import-required"> *</strong> : null}
                </span>
                <Input
                    type="dropdown"
                    id={`import-mapping-${field}`}
                    name={`import-mapping-${field}`}
                    placeholder="None"
                    value={mapping[field] || ""}
                    onChange={(event) => updateMapping(field, event.target.value)}
                    allowCustom={false}
                    colorScheme="light"
                    ariaLabel={`${label} options`}
                >
                    {({ closeMenu }) => (
                        <>
                            <button
                                type="button"
                                className="dropdown-menu-item"
                                onClick={() => {
                                    updateMapping(field, "");
                                    closeMenu();
                                }}
                            >
                                None
                            </button>
                            {preview.headers.map((header) => (
                                <button
                                    key={`${keyPrefix}-${header}`}
                                    type="button"
                                    className="dropdown-menu-item"
                                    onClick={() => {
                                        updateMapping(field, header);
                                        closeMenu();
                                    }}
                                >
                                    {header}
                                </button>
                            ))}
                        </>
                    )}
                </Input>
            </label>
        );
    }

    return (
        <Modal 
            isOpen={true} 
            onClose={onClose} 
            title="Import Contacts"
            icon={<LuImport />}
            wide={true}
            tall={true}
            actionButtons={actionButtons}
            >
                <div className="modal-form-container">
                    <input
                        ref={contactsFileInputRef}
                        type="file"
                        accept=".csv,.xlsx"
                        onChange={(event) => handleFileInputChange("contactsFile", event)}
                        className="import-hidden-input"
                    />

                    <div className="import-file-card">
                        <div className="import-file-card-details">
                            <p className="import-file-title">Contacts File</p>
                            {preview && (
                                <p className="import-selected-file">
                                    {preview.rowCount} row{preview.rowCount === 1 ? "" : "s"} detected (max {preview.maxRows})
                                </p>
                            )}

                        </div>
                        <div className="right-col">
                            <button type="button" className="import-upload-button" onClick={() => contactsFileInputRef.current?.click()}>
                                Choose file
                                <RiFolderUploadLine size={24} />
                            </button>
                            <p className="import-selected-file-hint">{selectedFile?.name || "Required"}</p>
                        </div>


                    </div>

                    {preview && (
                        <div className="import-mapping-sections">
                            <section className="import-mapping-section">
                                <div className="import-mapping-section-heading">
                                    <h3>Contact fields</h3>
                                    <span>Identity and account assignment</span>
                                </div>
                                <div className="import-mapping-grid">
                                {renderMappingField("name", "Name column", "name")}
                                {renderMappingField("first_name", "First name column", "first")}
                                {renderMappingField("last_name", "Last name column", "last")}
                                {renderMappingField("email", "Email column", "email")}
                                {renderMappingField("phone", "Phone column", "phone")}
                                {renderMappingField("account", "Account column", "account", true)}
                                {renderMappingField("job_title", "Job title column", "job")}
                                </div>
                            </section>
                            <section className="import-mapping-section">
                                <div className="import-mapping-section-heading">
                                    <h3>Account enrichment</h3>
                                    <span>Optional company details</span>
                                </div>
                                <div className="import-mapping-grid">
                                {renderMappingField("website", "Website column", "website")}
                                {renderMappingField("industry", "Industry column", "industry")}
                                {renderMappingField("subindustry", "Subindustry column", "subindustry")}
                                {renderMappingField("lead_source", "Lead source column", "source")}
                                </div>
                            </section>
                            <section className="import-mapping-section import-mapping-section--wide">
                                <div className="import-mapping-section-heading">
                                    <h3>LeadForge assessment</h3>
                                    <span>Sales context shared by contacts on the account</span>
                                </div>
                                <div className="import-mapping-grid">
                                {renderMappingField("lead_tier", "Lead tier column", "tier")}
                                {renderMappingField("primary_problem", "Primary problem column", "problem", true)}
                                {renderMappingField("problem_evidence", "Problem evidence column", "evidence")}
                                {renderMappingField("recommended_offer", "Recommended offer column", "offer")}
                                {renderMappingField("call_angle", "Call angle column", "angle")}
                                </div>
                            </section>
                        </div>
                    )}
                    <div className="import-helper-text-container">
                        <p className="import-helper-text">Required: an account column, a primary problem column, at least one name column, and one of email or phone.</p>

                        <p className="import-support-text">(Supported formats .csv,.xlsx; max file size 5 MB per file)</p>

                        <button type="button" className="import-sample-link" onClick={handleDownloadSample}>
                            Download a sample contacts CSV
                        </button>

                    </div>

                    {error && <p className="import-error-message">{error}</p>}

                    {result && (
                        <div className="import-result-box">
                            <p>
                                Imported {result.imported} contact{result.imported === 1 ? "" : "s"}.
                            </p>
                            <p>
                                Reused and enriched {result.reused || 0} contact{(result.reused || 0) === 1 ? "" : "s"}.
                            </p>
                            <p>
                                Skipped {result.skipped || 0} duplicate row{(result.skipped || 0) === 1 ? "" : "s"}.
                            </p>
                            <p>
                                Failed {result.failed} row{result.failed === 1 ? "" : "s"}.
                            </p>
                            {result.errors?.length > 0 && (
                                <ul className="import-error-list">
                                    {result.errors.slice(0, 5).map((item) => (
                                        <li key={`${item.row}-${item.message}`}>Row {item.row}: {item.message}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                </div>
        </Modal>
    );
}

export default ImportContacts